import React, { useCallback, useEffect, useId } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface AcademyOnboardingWizardStep {
  id: string;
  title: string;
  description?: string;
}

export interface AcademyOnboardingWizardProps {
  steps: AcademyOnboardingWizardStep[];
  currentStepIndex: number;
  onBack?: () => void;
  onNext?: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
  backLabel?: string;
  showBack?: boolean;
  isSubmitting?: boolean;
  children: React.ReactNode;
  footerExtra?: React.ReactNode;
  className?: string;
  /** Hide step counter and progress bar (e.g. pricing fast-track single-step flow). */
  hideProgress?: boolean;
}

export function AcademyOnboardingWizard({
  steps,
  currentStepIndex,
  onBack,
  onNext,
  nextDisabled = false,
  nextLabel = 'Continue',
  backLabel = 'Back',
  showBack = true,
  isSubmitting = false,
  children,
  footerExtra,
  className,
  hideProgress = false,
}: AcademyOnboardingWizardProps) {
  const progressId = useId();
  const currentStep = steps[currentStepIndex];
  const progressPercent = Math.round(((currentStepIndex + 1) / steps.length) * 100);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !nextDisabled && !isSubmitting && onNext) {
        const target = event.target as HTMLElement;
        if (target.tagName === 'TEXTAREA') return;
        event.preventDefault();
        onNext();
      }
    },
    [isSubmitting, nextDisabled, onNext]
  );

  useEffect(() => {
    const firstFocusable = document.getElementById(`wizard-step-${currentStep?.id}`);
    firstFocusable?.focus();
  }, [currentStep?.id]);

  if (!currentStep) return null;

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)} onKeyDown={handleKeyDown}>
      <div className="mb-6 space-y-3">
        {hideProgress ? null : (
          <>
            <div className="flex items-center justify-between gap-3 text-xs font-medium text-slate-500 dark:text-slate-400">
              <span>
                Step {currentStepIndex + 1} of {steps.length}
              </span>
              <span aria-live="polite">{progressPercent}%</span>
            </div>
            <div
              className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPercent}
              aria-labelledby={progressId}
            >
              <div
                className="h-full rounded-full bg-primary-600 transition-all duration-300 ease-out dark:bg-primary-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </>
        )}
        <div id={progressId}>
          <h2
            id={`wizard-step-${currentStep.id}`}
            tabIndex={-1}
            className="text-xl font-bold text-slate-900 outline-none dark:text-white"
          >
            {currentStep.title}
          </h2>
          {currentStep.description ? (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{currentStep.description}</p>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1">{children}</div>

      <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
        {footerExtra ? <div className="sm:order-2">{footerExtra}</div> : <div className="hidden sm:block" />}
        <div className="flex flex-col-reverse gap-3 sm:order-1 sm:flex-row">
          {showBack && currentStepIndex > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {backLabel}
            </Button>
          ) : null}
          <Button
            type="button"
            onClick={onNext}
            disabled={nextDisabled || isSubmitting}
            isLoading={isSubmitting}
            className="w-full sm:w-auto"
          >
            {nextLabel}
            {!isSubmitting ? <ArrowRight className="h-4 w-4" aria-hidden /> : null}
          </Button>
        </div>
      </div>
    </div>
  );
}
